import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/hooks/useToast";
import { VIDEO_TYPE_LABEL } from "@/types/video";
import { getHeroVideos, getVideoById } from "@/utils/video";
import DemoPlayer from "./components/DemoPlayer";
import RelatedList from "./components/RelatedList";

function DetailSkeleton() {
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 lg:px-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[260px_1fr]">
        <div className="aspect-[2/3] w-full max-w-[260px] animate-pulse rounded-xl bg-background-800"></div>
        <div className="space-y-4">
          <div className="h-8 w-2/3 animate-pulse rounded bg-background-800"></div>
          <div className="h-5 w-1/3 animate-pulse rounded bg-background-800"></div>
          <div className="h-24 w-full animate-pulse rounded bg-background-800"></div>
          <div className="h-10 w-52 animate-pulse rounded bg-background-800"></div>
        </div>
      </div>
      <div className="mt-8 aspect-video w-full animate-pulse rounded-xl bg-background-800"></div>
    </div>
  );
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [loading, setLoading] = useState(true);

  const video = id ? getVideoById(id) : undefined;

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [id]);

  useDocumentMeta({
    title: video ? `${video.title}免费在线观看 - 麻豆免费视频` : "内容不存在 - 麻豆免费视频",
    description: video ? video.summary : "该影视内容不存在或已下架。",
    keywords: video
      ? `${video.title},${video.genres.join(",")},${VIDEO_TYPE_LABEL[video.type]},免费在线观看,麻豆免费视频`
      : "麻豆免费视频",
    canonicalPath: `/detail/${id ?? ""}`,
  });

  if (loading) return <DetailSkeleton />;

  if (!video) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-20 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-accent-400">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </span>
        <h1 className="mt-6 text-2xl font-bold text-foreground-50">内容加载失败</h1>
        <p className="mt-3 text-sm text-foreground-400">我们找不到这部影视作品，它可能已被移除或链接有误。</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-home-5-line"></i>
            返回首页
          </Link>
          <Link
            to="/category/movie"
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-6 py-3 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
          >
            <i className="ri-film-line"></i>
            浏览电影
          </Link>
        </div>
      </div>
    );
  }

  const backdrop = getHeroVideos().find((item) => item.video.id === video.id)?.backdrop || video.poster;
  const favorited = isFavorite(video.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        showToast("链接已复制，快分享给好友吧", "ri-link");
      } else {
        showToast("分享链接：" + url, "ri-link");
      }
    } catch {
      showToast("分享失败，请手动复制地址栏链接", "ri-error-warning-line");
    }
  };

  return (
    <div>
      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-72 overflow-hidden md:h-80">
          <img
            src={backdrop}
            alt={`${video.title} 背景`}
            title={`${video.title} 高清在线观看背景`}
            className="h-full w-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/85 to-background-950"></div>
        </div>

        <div className="relative mx-auto w-full px-4 pt-8 md:px-6 md:pt-10 lg:px-10">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm">
            <Link to="/" className="transition-colors hover:text-primary-400">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${video.type}`} className="transition-colors hover:text-primary-400">
              {VIDEO_TYPE_LABEL[video.type]}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-200">{video.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[240px_1fr] md:gap-8 lg:grid-cols-[280px_1fr]">
            <div className="mx-auto w-full max-w-[240px] md:mx-0 md:max-w-none">
              <div className="relative overflow-hidden rounded-xl border border-background-800 bg-background-900">
                <div className="aspect-[2/3]">
                  <img
                    src={video.poster}
                    alt={video.title}
                    title={`${video.title} 海报`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-950">
                  {video.status}
                </span>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500/15 px-2.5 py-1 text-xs font-medium text-primary-400">
                  {VIDEO_TYPE_LABEL[video.type]}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-accent-500/15 px-2.5 py-1 text-xs font-medium text-accent-400">
                  <i className="ri-star-fill text-[10px]"></i>
                  {video.rating.toFixed(1)} 分
                </span>
                <span className="text-xs text-foreground-400">{video.year} 年 · {video.region}</span>
              </div>

              <h1 className="mt-3 text-2xl font-bold text-foreground-50 md:text-4xl">{video.title}</h1>

              <div className="mt-4 space-y-2 text-sm text-foreground-300 md:text-base">
                <p><span className="text-foreground-500">导演：</span>{video.director}</p>
                <p><span className="text-foreground-500">主演：</span>{video.cast.join(" / ")}</p>
                <p><span className="text-foreground-500">类型：</span>{video.genres.join(" / ")}</p>
                <p><span className="text-foreground-500">更新：</span>{video.updatedAt}</p>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {video.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category/${video.type}?genre=${encodeURIComponent(genre)}`}
                    className="whitespace-nowrap rounded-full border border-background-700 px-3 py-1 text-xs text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
                  >
                    #{genre}
                  </Link>
                ))}
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${video.type}?keyword=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full bg-secondary-500/20 px-3 py-1 text-xs text-secondary-300 transition-colors hover:bg-secondary-500/30"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href="#demo-player"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </a>
                <button
                  type="button"
                  onClick={() => {
                    toggleFavorite(video.id);
                    showToast(favorited ? "已取消收藏" : "已加入我的收藏（演示）", favorited ? "ri-heart-3-line" : "ri-heart-3-fill");
                  }}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-medium transition-colors ${
                    favorited
                      ? "border-primary-500 bg-primary-500/15 text-primary-400"
                      : "border-background-700 text-foreground-100 hover:border-primary-500 hover:text-primary-400"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex h-11 w-11 items-center justify-center rounded-md border border-background-700 text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
                  aria-label="分享"
                >
                  <i className="ri-share-forward-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-8 w-full space-y-8 px-4 md:px-6 lg:px-10">
        <div id="demo-player" className="scroll-mt-24">
          <DemoPlayer poster={video.poster} backdrop={backdrop} title={video.title} />
        </div>

        <section className="rounded-xl border border-background-800 bg-background-900 p-5 md:p-7">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground-100">
            <i className="ri-file-text-line text-primary-400"></i>
            剧情简介
          </h2>
          <p className="mt-4 text-sm leading-7 text-foreground-300 md:text-base">{video.summary}</p>
          <p className="mt-3 text-sm leading-7 text-foreground-400">
            《{video.title}》是一部{video.year}年的{video.region}{VIDEO_TYPE_LABEL[video.type]}作品，由{video.director}执导，{video.cast.join("、")}等主演。影片类型涵盖{video.genres.join("、")}，
            在麻豆免费视频可以了解该作品的详细信息，并查看相关推荐内容。
          </p>
        </section>

        <RelatedList video={video} />
      </div>
    </div>
  );
}